import React, { useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import VerifyModal from './VerifyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function TourSubscribe() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [city, setCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  const validate = () => {
    if (!firstName.trim()) return 'Please share your first name.';
    if (!email.trim()) return 'A valid email, please.';
    if (!EMAIL_RE.test(email.trim())) return 'That email doesn\u2019t look quite right.';
    if (!city.trim()) return 'Please tell me which city you\u2019d like to know about.';
    return null;
  };

  const requestCode = async () => {
    await axios.post(`${API}/verify/send`, {
      email: email.trim().toLowerCase(),
      first_name: firstName.trim() || null,
      purpose: 'subscribe',
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await requestCode();
      setVerifyError(null);
      setVerifyOpen(true);
    } catch (er) {
      const msg = er?.response?.data?.detail || 'Could not send verification code. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifySubmit = async (code) => {
    setVerifyError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        city_interest: city.trim(),
        verification_code: code,
      });
      setVerifyOpen(false);
      setDone(true);
      toast.success('You\u2019re on the list. See you soon xx');
    } catch (er) {
      const msg = er?.response?.data?.detail || 'That code didn\u2019t work. Please try again.';
      setVerifyError(typeof msg === 'string' ? msg : 'That code didn\u2019t work.');
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyResend = async () => {
    try {
      await requestCode();
      setVerifyError(null);
      toast.success('A new code is on its way.');
    } catch (er) {
      const msg = er?.response?.data?.detail || 'Could not send another code just yet.';
      setVerifyError(typeof msg === 'string' ? msg : 'Could not resend.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Toaster position="top-center" richColors closeButton />

      <VerifyModal
        open={verifyOpen}
        email={email.trim().toLowerCase()}
        firstName={firstName.trim() || null}
        purpose="subscribe"
        submitting={submitting}
        error={verifyError}
        onCancel={() => setVerifyOpen(false)}
        onSubmit={onVerifySubmit}
        onResend={onVerifyResend}
      />

      <div className="text-center mb-10">
        <div className="font-label text-zb-accent mb-4">Mailing list</div>
        <h3 className="font-serif text-3xl md:text-5xl text-zb-ink leading-tight">
          Be the <span className="italic text-zb-brand">first</span> to know.
        </h3>
        <p className="mt-5 text-zb-ink2 text-base max-w-xl mx-auto leading-relaxed">
          Be the first to know when I plan to visit your city. VIP status, and
          as always, minimal and discreet.
        </p>
      </div>

      {done ? (
        <div
          data-testid="subscribe-success"
          className="border border-zb-border bg-zb-bg2 p-10 text-center zb-fade-up"
        >
          <div className="font-label text-zb-accent mb-3">Thank you</div>
          <p className="font-serif text-2xl md:text-3xl text-zb-ink italic">
            You&rsquo;re on the list. See you soon xx
          </p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          data-testid="subscribe-form"
          noValidate
          className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
        >
          <div>
            <label className="font-label text-zb-accent block mb-1">First name</label>
            <input
              data-testid="subscribe-input-name"
              className="zb-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="What shall I call you?"
            />
          </div>
          <div>
            <label className="font-label text-zb-accent block mb-1">Email</label>
            <input
              data-testid="subscribe-input-email"
              type="email"
              className="zb-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@somewhere.com"
            />
          </div>
          <div className="md:col-span-2">
            <label className="font-label text-zb-accent block mb-1">City of interest</label>
            <input
              data-testid="subscribe-input-city"
              className="zb-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Sydney, Perth, Paris"
            />
          </div>
          <p className="md:col-span-2 -mt-2 text-[12px] text-zb-accent italic">
            I&rsquo;ll send a quick 6-digit code to your email before adding you.
          </p>
          <div className="md:col-span-2 pt-4 flex items-center justify-center">
            <button
              type="submit"
              disabled={submitting}
              data-testid="subscribe-submit-button"
              className="rounded-full bg-zb-brand hover:bg-zb-brandHover disabled:opacity-60 text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              {submitting ? 'Sending\u2026' : 'Join the list'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
